import React, { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { canResendClientNotice } from '../../utils/clientNotices';
import MailPreviewFrame, { MailActionPills, MailPreviewModel } from './MailPreviewFrame';
import './CorrespondenceChat.css';

export interface TimelineEntry {
  id: string;
  at: string;
  kind: string;
  sender: string;
  text: string;
  channel?: string;
  deliveryStatus?: string;
  noticeType?: string;
  amountMinor?: number;
  reference?: string;
  by?: string;
  changes?: string[];
  /** Studio view: the email notice that carried this message. */
  mailNoticeId?: string;
}

const COVERED_BY: Record<string, string> = {
  'studio-reply': 'studio-reply',
  'client-message': 'client-message',
  'payment-received': 'payment',
  'paid-in-full': 'payment',
  'shoot-confirmed': 'booking',
  'booking-updated': 'booking',
  'booking-secured': 'booking',
  'quotation-issued': 'quotation',
};

function when(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Johannesburg',
  }).format(date);
}

const ZONE = 'Africa/Johannesburg';

function dayKey(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONE }).format(date);
}

function dayLabel(value: string) {
  const key = dayKey(value);
  if (!key) return '';
  const now = new Date();
  if (key === dayKey(now.toISOString())) return 'Today';
  if (key === dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString())) return 'Yesterday';
  return new Intl.DateTimeFormat('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', timeZone: ZONE }).format(new Date(value));
}

function clock(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: ZONE }).format(date);
}

function deliveryLabel(status?: string) {
  if (status === 'captured') return 'Email disabled / stored locally';
  if (status === 'not_configured') return 'Email not configured';
  if (status === 'sent') return 'Accepted by provider — delivery not confirmed';
  if (status === 'failed') return 'Email failed';
  if (status === 'queued') return 'Email queued';
  if (status === 'preview') return 'Message saved (preview)';
  if (status === 'delivered') return 'Email delivery confirmed';
  return status || '';
}

function isSystem(entry: TimelineEntry) {
  return entry.kind === 'quotation' || entry.kind === 'booking' || entry.kind === 'payment' || entry.kind === 'payment-audit' || entry.kind === 'notice';
}

function TimelineItem({
  entry,
  chat = false,
  perspective = 'studio',
  studio = false,
  mailBusy = '',
  mailSending = '',
  onPreview,
  onResend,
}: {
  entry: TimelineEntry;
  chat?: boolean;
  perspective?: 'studio' | 'client';
  studio?: boolean;
  mailBusy?: string;
  mailSending?: string;
  onPreview?: (entry: TimelineEntry) => void;
  onResend?: (noticeId: string) => void;
}) {
  const noticeId = String(entry.id || '').startsWith('notice:') ? String(entry.id).slice('notice:'.length) : (entry.mailNoticeId || '');
  const pills = studio && noticeId && onPreview ? (
    <MailActionPills
      noticeId={noticeId}
      canSend={entry.kind === 'studio-reply' || (entry.kind === 'notice' && canResendClientNotice(entry.noticeType))}
      busy={mailBusy === noticeId}
      sending={mailSending === noticeId}
      onPreview={() => onPreview(entry.kind === 'notice' ? entry : { ...entry, id: `notice:${noticeId}`, noticeType: entry.kind === 'studio-reply' ? 'studio-reply' : 'client-message' })}
      onResend={onResend ? () => onResend(noticeId) : undefined}
    />
  ) : null;
  if (isSystem(entry)) {
    if (chat) {
      return (
        <p className="quote-event quote-chat-event">
          <span>{entry.text}</span>
          <time dateTime={entry.at}>{clock(entry.at)}</time>
          {pills}
        </p>
      );
    }
    return (
      <p className="quote-event">
        <time dateTime={entry.at}>{when(entry.at)}</time>
        <span>{entry.text}</span>
      </p>
    );
  }
  const mine = entry.kind === 'studio-reply';
  const client = entry.kind === 'client-message';
  if (chat) {
    // Your own messages sit on the right, the other person's on the left.
    const own = perspective === 'client' ? client : mine;
    const side = own ? ' is-own' : (mine || client) ? ' is-other' : ' is-note';
    return (
      <article className={`quote-bubble quote-chat-bubble${mine ? ' is-studio' : ''}${client ? ' is-client' : ''}${side}`}>
        {!mine && !client ? <strong className="quote-chat-sender">{entry.sender}</strong> : null}
        <p>{entry.text}</p>
        {entry.mailNoticeId ? <p className="quote-timeline-meta">{entry.kind === 'studio-reply' ? 'Email to the client' : 'Email to the studio'}</p> : null}
        {entry.reference ? <p className="quote-timeline-meta">Reference {entry.reference}</p> : null}
        {entry.channel === 'whatsapp-note' ? <p className="quote-timeline-meta">Recorded by Carmen. This was not imported from WhatsApp.</p> : null}
        <time dateTime={entry.at}>{clock(entry.at)}</time>
        {pills}
      </article>
    );
  }
  return (
    <article className={`quote-bubble${mine ? ' is-studio' : ''}${client ? ' is-client' : ''}${!mine && !client ? ' is-note' : ''}`}>
      <header>
        <strong>{entry.sender}</strong>
        <time dateTime={entry.at}>{when(entry.at)}</time>
      </header>
      <p>{entry.text}</p>
      {entry.reference ? <p className="quote-timeline-meta">Reference {entry.reference}</p> : null}
      {entry.channel === 'whatsapp-note' ? <p className="quote-timeline-meta">Recorded by Carmen. This was not imported from WhatsApp.</p> : null}
    </article>
  );
}

function organise(entries: TimelineEntry[], studio: boolean, oldestFirst = false) {
  const kinds = new Set(entries.map((entry) => entry.kind));
  const conversation: TimelineEntry[] = [];
  const emailLog: TimelineEntry[] = [];
  entries.forEach((entry) => {
    if (entry.kind !== 'notice') {
      conversation.push(entry);
      return;
    }
    const covered = Boolean(entry.noticeType && COVERED_BY[entry.noticeType] && kinds.has(COVERED_BY[entry.noticeType]));
    if (studio) emailLog.push(entry);
    if (!covered) conversation.push(entry);
  });
  conversation.sort((left, right) => (oldestFirst
    ? String(left.at || '').localeCompare(String(right.at || ''))
    : String(right.at || '').localeCompare(String(left.at || ''))));
  emailLog.sort((left, right) => String(right.at || '').localeCompare(String(left.at || '')));
  return { conversation, emailLog };
}

const CorrespondenceTimeline = ({
  entries,
  separateInternal = false,
  studio = false,
  conversationOnly = false,
  chat = false,
  perspective = 'studio',
}: {
  entries: TimelineEntry[];
  separateInternal?: boolean;
  studio?: boolean;
  deferBooking?: boolean;
  /** Client view: only what was said, not the system notes about quotes, bookings, or payments. */
  conversationOnly?: boolean;
  /** Messaging layout: oldest first, newest at the bottom, grouped by day, client left and Carmen right. */
  chat?: boolean;
  /** Whose screen this is. Decides which side of the chat is "mine". */
  perspective?: 'studio' | 'client';
}) => {
  const internal = separateInternal ? entries.filter((entry) => entry.kind === 'internal-note') : [];
  const scoped = conversationOnly
    ? entries.filter((entry) => entry.kind === 'client-message' || entry.kind === 'studio-reply')
    : entries;
  const visible = separateInternal ? scoped.filter((entry) => entry.kind !== 'internal-note') : scoped;
  const { conversation, emailLog } = organise(visible, studio, chat);
  const [mailBusy, setMailBusy] = useState('');
  const [mailSending, setMailSending] = useState('');
  const [mailNote, setMailNote] = useState('');
  const [mail, setMail] = useState<MailPreviewModel | null>(null);
  const noticeIdOf = (entry: TimelineEntry) => (
    String(entry.id || '').startsWith('notice:') ? String(entry.id).slice('notice:'.length) : ''
  );
  const openMail = async (entry: TimelineEntry) => {
    const noticeId = noticeIdOf(entry);
    if (!noticeId || mailBusy || mailSending) return;
    setMailBusy(noticeId);
    setMailNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/notices/${noticeId}/preview`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.html) {
        setMailNote(body.error || 'The email could not be opened.');
        return;
      }
      setMail({
        id: noticeId,
        subject: body.subject || 'Email',
        to: body.to || '',
        html: body.html,
        canSend: canResendClientNotice(entry.noticeType),
      });
    } catch {
      setMailNote('The email could not be opened.');
    } finally {
      setMailBusy('');
    }
  };
  const resendMail = async (noticeId: string) => {
    if (!noticeId || mailSending) return;
    setMailSending(noticeId);
    setMailNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/notices/${noticeId}/retry`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      setMailNote(response.ok ? 'Sent.' : (body.error || 'It did not send.'));
    } catch {
      setMailNote('It did not send.');
    } finally {
      setMailSending('');
    }
  };
  if (!chat && !conversation.length && !internal.length && !emailLog.length) {
    return <p className="quote-timeline-empty">{conversationOnly ? 'No messages yet.' : 'Messages and updates will appear here.'}</p>;
  }
  return (
    <div className={`quote-timeline${chat ? ' is-chat' : ''}`}>
      {chat ? (
        <div className="quote-chat-thread" role="log" aria-label="Conversation">
          {conversation.map((entry, index) => (
            <React.Fragment key={entry.id}>
              {index === 0 || dayKey(entry.at) !== dayKey(conversation[index - 1].at) ? (
                <p className="quote-chat-day"><span>{dayLabel(entry.at)}</span></p>
              ) : null}
              <TimelineItem
                entry={entry}
                chat
                perspective={perspective}
                studio={studio}
                mailBusy={mailBusy}
                mailSending={mailSending}
                onPreview={openMail}
                onResend={resendMail}
              />
            </React.Fragment>
          ))}
          {conversation.length === 0 ? <p className="quote-chat-empty">{perspective === 'client' ? 'No messages yet. Write to Carmen below.' : 'No messages yet. Write below to start the conversation.'}</p> : null}
        </div>
      ) : conversation.map((entry) => <TimelineItem key={entry.id} entry={entry} />)}
      {studio && emailLog.length > 0 && (
        <details className="quote-email-log">
          <summary>Email delivery ({emailLog.length}, {emailLog.filter((entry) => entry.deliveryStatus === 'sent' || entry.deliveryStatus === 'delivered').length} sent)</summary>
          {emailLog.map((entry) => {
            const noticeId = noticeIdOf(entry);
            return (
              <p key={`log-${entry.id}`} className="quote-event">
                <time dateTime={entry.at}>{when(entry.at)}</time>
                <span className="quote-mail-copy">
                  <span>{entry.noticeType || 'Email'} · {deliveryLabel(entry.deliveryStatus) || 'Recorded'}</span>
                  {entry.text ? <span className="quote-mail-text">{entry.text}</span> : null}
                  {noticeId ? (
                    <MailActionPills
                      noticeId={noticeId}
                      canSend={canResendClientNotice(entry.noticeType)}
                      busy={mailBusy === noticeId}
                      sending={mailSending === noticeId}
                      onPreview={() => openMail(entry)}
                      onResend={canResendClientNotice(entry.noticeType) ? () => resendMail(noticeId) : undefined}
                    />
                  ) : null}
                </span>
              </p>
            );
          })}
          {mailNote && !mail ? <p className="studio-note">{mailNote}</p> : null}
        </details>
      )}
      {internal.length > 0 && (
        <section className="quote-internal-notes" aria-label="Private notes">
          <h3>Private notes</h3>
          <p>These notes stay in the studio. Clients do not see them.</p>
          {internal.map((entry) => <TimelineItem key={entry.id} entry={entry} />)}
        </section>
      )}
      {mail ? (
        <MailPreviewFrame
          preview={mail}
          note={mailNote}
          sending={mailSending === mail.id}
          onSend={mail.canSend ? () => resendMail(mail.id) : undefined}
          onClose={() => { if (!mailSending) setMail(null); }}
        />
      ) : null}
    </div>
  );
};

export default CorrespondenceTimeline;
